/**
 * Datadog Provider Component
 * Initializes Datadog RUM (Real User Monitoring) and Browser Logs
 * for frontend observability, error tracking, and session replay.
 *
 * Session Replay is enabled by default for customer support debugging.
 * All recordings are HIPAA-compliant with automatic PHI/PII masking.
 */

import React, { useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

// Configuration from environment variables
const DD_APPLICATION_ID = import.meta.env.VITE_DD_APPLICATION_ID;
const DD_CLIENT_TOKEN = import.meta.env.VITE_DD_CLIENT_TOKEN;
const DD_SITE = import.meta.env.VITE_DD_SITE ?? 'datadoghq.com';
const DD_SERVICE = import.meta.env.VITE_DD_SERVICE ?? 'trust-marketplace-frontend';
const DD_ENABLED = import.meta.env.VITE_DD_ENABLED !== 'false';
// Session replay is enabled by default for customer support (opt-out with 'false')
const DD_SESSION_REPLAY = import.meta.env.VITE_DD_SESSION_REPLAY_ENABLED !== 'false';
// Percentage of sessions that get a replay recording. Replay is the expensive
// half of RUM, and it ran at 100% of 100% until 2026-09-29. Sampled sessions
// still capture every view, action, error and resource — only the video-like
// replay is sampled — so support keeps a representative pool of recordings
// without every session being filmed. Raise temporarily (VITE_DD_SESSION_
// REPLAY_SAMPLE_RATE=100) when chasing a reproduction, then put it back.
// Requires a rebuild + redeploy: Vite inlines this at BUILD time.
const DD_SESSION_REPLAY_SAMPLE_RATE = (() => {
  // Guard the empty string explicitly: Number('') is 0, so an env var that is
  // present but blank would silently switch replay off rather than fall back.
  const configured = import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE;
  if (configured === undefined || configured === null || String(configured).trim() === '') return 20;
  const raw = Number(configured);
  return Number.isFinite(raw) && raw >= 0 && raw <= 100 ? raw : 20;
})();
const IS_DEV = import.meta.env.DEV;

// HIPAA-compliant CSS selectors for masking
const HIPAA_MASK_SELECTORS = [
  // PHI/PII data markers
  '[data-sensitive]',
  '[data-phi]',
  '[data-pii]',
  '.phi-data',
  '.pii-data',
  '.sensitive-data',
  // Form fields that may contain PHI
  'input[type="email"]',
  'input[type="tel"]',
  'input[type="password"]',
  'input[name*="ssn"]',
  'input[name*="social"]',
  'input[name*="dob"]',
  'input[name*="birth"]',
  'input[name*="medical"]',
  'input[name*="health"]',
  'input[name*="diagnosis"]',
  'input[name*="medication"]',
  'input[name*="insurance"]',
  'input[name*="member_id"]',
  'input[name*="policy"]',
  // Textareas that may contain medical notes
  'textarea[name*="notes"]',
  'textarea[name*="medical"]',
  'textarea[name*="diagnosis"]',
  'textarea[name*="history"]',
  // Common healthcare-related class patterns
  '.member-info',
  '.patient-info',
  '.medical-record',
  '.health-info',
  '.insurance-info',
  // Member portal specific selectors
  '.member-ssn',
  '.member-dob',
  '.member-address',
  '.member-phone',
  '.member-email',
  '.enrollee-info',
];

// Determine environment
const getEnvironment = (): string => {
  if (IS_DEV) return 'development';
  if (window.location.hostname.includes('staging')) return 'staging';
  if (window.location.hostname.includes('dev.')) return 'staging';
  return 'production';
};

// Check if Datadog should be initialized
const shouldInitialize = (): boolean => {
  console.log("[Datadog] Checking should initialize");
  if (!DD_ENABLED) return false;
  if (!DD_CLIENT_TOKEN) {
    if (IS_DEV) {
      console.info('[Datadog] Disabled - no client token configured');
    }
    return false;
  }
  return true;
};

/**
 * Initialize Datadog RUM and Logs
 */
export function initDatadog(): void {
  if (!shouldInitialize()) return;

  const env = getEnvironment();

  try {
    // Initialize RUM (Real User Monitoring)
    if (DD_APPLICATION_ID) {
      datadogRum.init({
        applicationId: DD_APPLICATION_ID,
        clientToken: DD_CLIENT_TOKEN,
        site: DD_SITE,
        service: DD_SERVICE,
        env: env,
        version: import.meta.env.VITE_APP_VERSION ?? '1.0.0',
        // Every session is tracked (views, actions, errors, resources) —
        // that is the cheap, complete signal and the one the monitors read.
        sessionSampleRate: 100,
        // Only a sample of those sessions is also REPLAY-recorded. SDK v5
        // honours this on its own; startSessionReplayRecording() below does
        // not override it unless called with { force: true }.
        sessionReplaySampleRate: DD_SESSION_REPLAY ? DD_SESSION_REPLAY_SAMPLE_RATE : 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        // HIPAA-compliant privacy settings
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: [
          // Allow tracing to your backend APIs
          { match: /https:\/\/.*\.weavein\.ai/, propagatorTypes: ['tracecontext', 'datadog'] },
          { match: /https:\/\/.*\.onrender\.com/, propagatorTypes: ['tracecontext', 'datadog'] },
          { match: /http:\/\/localhost:8000/, propagatorTypes: ['tracecontext', 'datadog'] },
        ],
        beforeSend: (event) => {
          // Sanitize sensitive data before sending
          if (event.view?.url) {
            // Remove sensitive query parameters
            const sensitiveParams = ['token', 'password', 'secret', 'key', 'auth', 'ssn', 'dob', 'member_id'];
            try {
              const url = new URL(event.view.url);
              sensitiveParams.forEach(param => url.searchParams.delete(param));
              event.view.url = url.toString();
            } catch {
              // URL parsing failed, keep original
            }
          }
          return true;
        },
      });

      // Start session replay if enabled (default: true)
      if (DD_SESSION_REPLAY) {
        datadogRum.startSessionReplayRecording({
          // Force masking of specific HIPAA-sensitive elements
          // This overrides any defaultPrivacyLevel settings for these selectors
        });

        if (IS_DEV) {
          console.info('[Datadog] Session Replay enabled with HIPAA masking', {
            sampleRate: DD_SESSION_REPLAY_SAMPLE_RATE,
          });
        }
      }

      // Expose the RUM instance globally so the embedded support widget
      // (public/widgets/support-widget.js, runs in the page but has no module
      // import) can attach the live session id + replay link to a submitted
      // ticket via window.DD_RUM.getInternalContext(). Without this the npm
      // module is not on window and every ticket recorded a null Datadog session.
      if (typeof window !== 'undefined') {
        (window as any).DD_RUM = datadogRum;
      }

      if (IS_DEV) {
        console.info('[Datadog RUM] Initialized', {
          env,
          service: DD_SERVICE,
          sessionReplay: DD_SESSION_REPLAY,
          sessionReplaySampleRate: DD_SESSION_REPLAY ? DD_SESSION_REPLAY_SAMPLE_RATE : 0,
          privacyLevel: 'mask-user-input'
        });
      }
    }

    // Initialize Browser Logs
    datadogLogs.init({
      clientToken: DD_CLIENT_TOKEN,
      site: DD_SITE,
      service: DD_SERVICE,
      env: env,
      version: import.meta.env.VITE_APP_VERSION ?? '1.0.0',
      forwardErrorsToLogs: true,
      forwardConsoleLogs: IS_DEV ? [] : ['error', 'warn'], // Only forward errors/warns in prod
      forwardReports: 'all',
      sessionSampleRate: 100,
      beforeSend: (log) => {
        // Sanitize sensitive data from logs
        if (log.message) {
          // Remove potential PII patterns
          log.message = log.message
            .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
            .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN]')
            .replace(/\b\d{16}\b/g, '[CARD]')
            .replace(/\b\d{10}\b/g, '[PHONE]')
            .replace(/\b(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}\b/g, '[DOB]');
        }
        return true;
      },
    });

    if (IS_DEV) {
      console.info('[Datadog Logs] Initialized', { env, service: DD_SERVICE });
    }
  } catch (error) {
    console.error('[Datadog] Failed to initialize:', error);
  }
}

/**
 * Set user information for tracking
 * Note: Do NOT include PHI/PII like SSN, DOB, or medical information
 */
export function setDatadogUser(user: {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  organizationId?: string;
  organizationName?: string;
  /** TRUS-747: set while a super admin is viewing as this user. */
  impersonatorEmail?: string;
  impersonationSessionId?: string;
}): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.setUser({
      id: user.id,
      email: user.email,
      name: user.name,
      // Custom attributes (safe for HIPAA)
      role: user.role,
      organizationId: user.organizationId,
      organizationName: user.organizationName,
      ...(user.impersonatorEmail
        ? { impersonatorEmail: user.impersonatorEmail, impersonationSessionId: user.impersonationSessionId }
        : {}),
    });
  } catch (error) {
    console.debug('[Datadog] Failed to set user:', error);
  }
}

/**
 * Clear user information (e.g., on logout)
 */
export function clearDatadogUser(): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.clearUser();
  } catch (error) {
    console.debug('[Datadog] Failed to clear user:', error);
  }
}

/**
 * Log custom events
 */
export const ddLog = {
  debug: (message: string, context?: Record<string, unknown>) => {
    if (!shouldInitialize()) return;
    datadogLogs.logger.debug(message, context);
  },
  info: (message: string, context?: Record<string, unknown>) => {
    if (!shouldInitialize()) return;
    datadogLogs.logger.info(message, context);
  },
  warn: (message: string, context?: Record<string, unknown>) => {
    if (!shouldInitialize()) return;
    datadogLogs.logger.warn(message, context);
  },
  error: (message: string, context?: Record<string, unknown>) => {
    if (!shouldInitialize()) return;
    datadogLogs.logger.error(message, context);
  },
};

/**
 * Track custom actions
 */
export function trackAction(name: string, context?: Record<string, unknown>): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.debug('[Datadog] Failed to track action:', error);
  }
}

/**
 * Track custom errors
 */
export function trackError(error: Error, context?: Record<string, unknown>): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.addError(error, context);
  } catch (err) {
    console.debug('[Datadog] Failed to track error:', err);
  }
}

/**
 * Add global context that will be attached to all RUM events
 */
export function setGlobalContext(key: string, value: unknown): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.setGlobalContextProperty(key, value);
  } catch (error) {
    console.debug('[Datadog] Failed to set global context:', error);
  }
}

/**
 * Get the current session ID for linking to replays
 */
export function getSessionId(): string | undefined {
  if (!shouldInitialize()) return undefined;

  try {
    const context = datadogRum.getInternalContext();
    return context?.session_id;
  } catch {
    return undefined;
  }
}

/**
 * Get the URL to view the current session replay in Datadog
 */
export function getSessionReplayUrl(): string | undefined {
  const sessionId = getSessionId();
  if (!sessionId) return undefined;

  const env = getEnvironment();
  return `https://app.datadoghq.com/rum/replay/sessions/${sessionId}?env=${env}&service=${DD_SERVICE}`;
}

/**
 * Check if session replay is currently recording
 */
export function isSessionReplayRecording(): boolean {
  if (!shouldInitialize() || !DD_SESSION_REPLAY) return false;

  try {
    const context = datadogRum.getInternalContext();
    return !!context?.session_id;
  } catch {
    return false;
  }
}

/**
 * Stop session replay recording (e.g., for privacy-sensitive flows)
 */
export function stopSessionReplay(): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.stopSessionReplayRecording();
  } catch (error) {
    console.debug('[Datadog] Failed to stop session replay:', error);
  }
}

/**
 * Start/resume session replay recording
 */
export function startSessionReplay(): void {
  if (!shouldInitialize()) return;

  try {
    datadogRum.startSessionReplayRecording();
  } catch (error) {
    console.debug('[Datadog] Failed to start session replay:', error);
  }
}

/**
 * HIPAA mask selectors export for use in components
 */
export const HIPAA_SELECTORS = HIPAA_MASK_SELECTORS;

interface DatadogProviderProps {
  children: React.ReactNode;
}

/**
 * Datadog Provider Component
 * Initializes Datadog on mount and provides context to the app
 */
export const DatadogProvider: React.FC<DatadogProviderProps> = ({ children }) => {
  const initialized = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (!initialized.current) {
      initialized.current = true;
      initDatadog();
    }
  }, []);

  // This provider doesn't add any context, just initializes Datadog
  return <>{children}</>;
};

export default DatadogProvider;
